import { type FC } from 'react'

import {
  Box,
  TagColor as Color,
  Hint,
  Icon,
  Link,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import groupBy from 'lodash/groupBy'
import map from 'lodash/map'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import ColoredTag from 'modules/org_chart/components/ColoredTag'
import { useOrgChartState } from 'modules/org_chart/components/Provider'
import {
  type GraphQLEmployee,
  employeeDirectlyManagedEmployees,
} from 'modules/org_chart/lib/employeeUtils'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import Avatar from './Avatar'

const DirectReports: FC<{
  allEmployees: GraphQLEmployee[]
}> = ({ allEmployees }) => {
  const { navigation } = useNavigation()
  const [isTooltipOpen, toggleIsTooltipOpen] = useToggle(false)
  const { update, activeEmployee } = useOrgChartState()

  const managedEmployees = activeEmployee
    ? employeeDirectlyManagedEmployees(activeEmployee, allEmployees)
    : []

  const employeeId = activeEmployee?.id

  useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job'),
        // Note: GraphQL employees don't have policy interface, skip for now
        // policyAuthorizations.authorizePolicies('contracts.assign_jobs', managedEmployees),
      ]),
    [employeeId]
  )

  if (!managedEmployees?.length) return null

  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')
  const groupedEmployeesByRole = groupBy(
    managedEmployees,
    (x) => x.referenceContract?.jobCatalogRole || 'undefined'
  )
  // Note: GraphQL employees don't have policy interface, assume all can be assigned roles
  const unnasignedRoleEmployeesIds = managedEmployees
    .filter((x) => !x.referenceContract?.jobCatalogRole)
    .map((x) => x.id)
  const assignRoleRoute = `${navigation.employees.jobCatalog.bulkEdit.fullPath()}?employeesIds=${JSON.stringify(unnasignedRoleEmployeesIds)}&state=unnasigned`

  const avatarsByJob = map(groupedEmployeesByRole, (employees, role) => {
    const isRoleUnnasigned = role === 'undefined'
    const roleName = role === 'undefined' ? i18n.t('employees.orgchart.unassigned_role') : role
    const avatars = employees.map((y) => {
      return (
        <Box key={y.id} cursor={'pointer'} onClick={() => handleOnAvatarClick(y)}>
          <Avatar key={y.id} url={y.avatar?.url || ''} label={y.fullName} />
        </Box>
      )
    })

    return (
      <Box flexDirection="column" gap="s16" alignItems="flexStart" key={roleName}>
        <ColoredTag color={isRoleUnnasigned ? Color.grey : undefined}>
          {`${roleName} (${employees.length})`}
        </ColoredTag>
        <Box flexDirection="row" gap="s16" flexWrap="wrap">
          {avatars}
        </Box>
      </Box>
    )
  })

  const handleOnAvatarClick = (employee: GraphQLEmployee) => {
    update('activeEmployee', employee)
    update('activeSidePanelTab', 'profile')
  }

  return (
    <Box padding="s24" gap="s16" flexDirection="column" alignItems="flexStart">
      <Box flexDirection="row" alignItems="center" gap="s6">
        <Text.Header2>
          {i18n.t('employees.orgchart.direct_reports')} ({managedEmployees.length})
        </Text.Header2>
        <Tooltip.Alt
          open={isTooltipOpen}
          toggle={toggleIsTooltipOpen}
          attachment="top center"
          label={i18n.t('employees.orgchart.direct_reports_tooltip', {
            name: activeEmployee?.fullName || '',
          })}
        >
          <Icon.Medium icon="info-circle-line" color="grey800" />
        </Tooltip.Alt>
      </Box>
      <Box gap="s24">
        <Box gap="s32">{avatarsByJob}</Box>
        <Hint.Popover
          step={2}
          title={i18n.t('employees.orgchart.hint.pop_over_title')}
          description={i18n.t('employees.orgchart.hint.pop_over_description')}
          actionLabel={i18n.t('common.done')}
          position="top left"
          maxWidth="s300"
        >
          <Box gap="s8">
            {!!unnasignedRoleEmployeesIds.length && (
              <Link.Router to={assignRoleRoute}>
                {i18n.t('employees.orgchart.sidepanel.assign_them_a_role')}
              </Link.Router>
            )}
            {canCreateJob && (
              <Link.Router
                to={`${navigation.employees.organizationChart.addJobCatalogRole.fullPath()}?fallbackRoute=${navigation.employees.organizationChart.jobCatalogRoleCreated.fullPath()}`}
              >
                {i18n.t('employees.orgchart.sidepanel.create_a_new_role')}
              </Link.Router>
            )}
          </Box>
        </Hint.Popover>
      </Box>
    </Box>
  )
}

export default DirectReports
